import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BellOff, Loader2, CheckCheck } from 'lucide-react';
import NotificationItem from '../components/notifications/NotificationItem';
import {
  useGetNotificationsQuery,
  useMarkReadMutation,
  useMarkAllReadMutation,
  useDeleteNotificationMutation,
} from '../services/notificationsApi';
import { setUnreadCount } from '../features/notificationsSlice';
import styles from '../css/Notifications.module.css';

const PER_PAGE = 20;

const TABS = [
  { key: 'unread', label: 'Непрочитанные', isRead: false },
  { key: 'read',   label: 'Прочитанные',   isRead: true  },
];

const Notifications = () => {
  const dispatch = useDispatch();
  const unreadCount = useSelector((s) => s.notifications.unreadCount);

  const [activeTab, setActiveTab] = useState('unread');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const hasMoreRef = useRef(false);
  const isFetchingRef = useRef(false);
  const hasScrolledRef = useRef(false);
  const observerRef = useRef(null);

  const currentTab = TABS.find((t) => t.key === activeTab);

  const { data, isLoading, isFetching } = useGetNotificationsQuery({
    page,
    perPage: PER_PAGE,
    isRead: currentTab.isRead,
  });

  const [markRead] = useMarkReadMutation();
  const [markAllRead] = useMarkAllReadMutation();
  const [deleteNotification] = useDeleteNotificationMutation();

  isFetchingRef.current = isFetching;

  // Сброс при смене вкладки
  useEffect(() => {
    setItems([]);
    setPage(1);
    hasMoreRef.current = false;
    hasScrolledRef.current = false;
  }, [activeTab]);

  // Накапливаем уведомления при подгрузке
  useEffect(() => {
    if (!data?.data?.items) return;
    setItems((prev) => {
      const ids = new Set(prev.map((i) => i.id));
      const fresh = data.data.items.filter((i) => !ids.has(i.id));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
    const total = data.meta?.total ?? 0;
    hasMoreRef.current = page * PER_PAGE < total;
  }, [data, page]);

  // Отслеживаем скролл
  useEffect(() => {
    const mark = () => { hasScrolledRef.current = true; };
    window.addEventListener('scroll', mark, { passive: true });
    window.addEventListener('wheel', mark, { passive: true });
    window.addEventListener('touchmove', mark, { passive: true });
    return () => {
      window.removeEventListener('scroll', mark);
      window.removeEventListener('wheel', mark);
      window.removeEventListener('touchmove', mark);
    };
  }, []);

  // IntersectionObserver sentinel
  const sentinelRef = useRef((el) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          hasScrolledRef.current &&
          !isFetchingRef.current &&
          hasMoreRef.current
        ) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: '200px' }
    );
    observerRef.current.observe(el);
  }).current;

  const handleMarkRead = async (id) => {
    try {
      await markRead(id);
      // На вкладке "непрочитанные" — удаляем из списка после пометки
      if (activeTab === 'unread') {
        setItems((prev) => prev.filter((n) => n.id !== id));
      } else {
        setItems((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
      }
      dispatch(setUnreadCount(Math.max(0, unreadCount - 1)));
    } catch {}
  };

  const handleDelete = async (id) => {
    const item = items.find((n) => n.id === id);
    try {
      await deleteNotification(id);
      setItems((prev) => prev.filter((n) => n.id !== id));
      if (item && !item.is_read) {
        dispatch(setUnreadCount(Math.max(0, unreadCount - 1)));
      }
    } catch {}
  };

  const handleMarkAll = async () => {
    try {
      await markAllRead();
      setItems([]);
      dispatch(setUnreadCount(0));
    } catch {}
  };

  const handleTabChange = (key) => {
    if (key !== activeTab) setActiveTab(key);
  };

  return (
    <div className={styles.notificationsPage}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Уведомления</h1>
      </div>

      {/* Переключатель */}
      <div className={styles.tabsRow}>
        <div className={styles.switchWrapper}>
          <button
            className={`${styles.switchTrack} ${activeTab === 'read' ? styles.switchTrackRead : ''}`}
            onClick={() => handleTabChange(activeTab === 'unread' ? 'read' : 'unread')}
            aria-label="Переключить фильтр"
          >
            <span className={`${styles.switchThumb} ${activeTab === 'read' ? styles.switchThumbRead : ''}`} />
          </button>
          <span className={styles.switchLabel}>
            {activeTab === 'unread' ? (
              <>
                Непрочитанные
                {unreadCount > 0 && (
                  <span className={styles.tabBadge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </>
            ) : 'Прочитанные'}
          </span>
        </div>

        <button
          className={styles.markAllBtn}
          onClick={handleMarkAll}
          style={{ visibility: activeTab === 'unread' ? 'visible' : 'hidden' }}
        >
          Прочитать все
          <CheckCheck size={14} strokeWidth={2.5} />
        </button>
      </div>

      {isLoading && items.length === 0 && (
        <div className={styles.loadingState}>
          <Loader2 size={24} className={styles.spinner} />
        </div>
      )}

      {items.length > 0 && (
        <div className={styles.list}>
          {items.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              onMarkRead={handleMarkRead}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <div ref={sentinelRef} className={styles.sentinel}>
        {isFetching && items.length > 0 && (
          <Loader2 size={20} className={styles.spinner} />
        )}
      </div>

      {!isLoading && items.length === 0 && (
        <div className={styles.emptyState}>
          <BellOff size={40} strokeWidth={1.3} />
          <p className={styles.emptyTitle}>
            {activeTab === 'unread' ? 'Непрочитанных нет' : 'Прочитанн��х нет'}
          </p>
          <p className={styles.emptyDesc}>
            {activeTab === 'unread'
              ? 'Когда выйдет новая серия из закладок — вы узнаете здесь'
              : 'Прочитанные уведомления появятся здесь'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Notifications;
